import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Typography, Stack, Chip, TextField, MenuItem, Alert, LinearProgress, Table, TableHead,
  TableRow, TableCell, Paper, InputAdornment, Checkbox, Button, Tooltip,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import { TableBody as MuiTableBody } from '@mui/material'; // side-by-side attributes: never paged
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { FAMILY_VENDOR_OFFERINGS_QUERY, VENDOR_OFFERING_FIELD_DEFS_QUERY } from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { packageLabelsFor } from '../components/OfferingPackages';
import { BookVendorDialog } from '../components/VendorBooking';

const inr = (n) => (n == null ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`);
// Per-plate / per-person prices scale with the plates; others (e.g. per event) are a flat price
const PER_UNIT = /\bper (plate|guest|person|head)/i;
const MAX_COMPARE = 3;

const SORTS = [
  { value: 'total', label: 'Total cost: low to high' },
  { value: 'plate', label: 'Price per plate: low to high' },
  { value: 'dishes', label: 'Most dishes' },
  { value: 'caterer', label: 'Caterer: A to Z' },
];

// Every menu of every contracted caterer, compared on the cost for the family's number of
// plates, the dishes and the caterer's details. Families see the caterers on their signed
// contract; admins/matchmakers see all (or the selected family's).
export function MenuComparison() {
  const { user } = useAuth();
  const { family } = useFamily();
  const isFamilyUser = ['bride_family', 'groom_family'].includes(user?.role?.name);
  const canBook = isFamilyUser || Boolean(family);

  const [plates, setPlates] = useState('500');
  const [foodType, setFoodType] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total');
  const [selected, setSelected] = useState([]); // menu keys, up to 3
  const [booking, setBooking] = useState(null);
  const [booked, setBooked] = useState(null);

  const { data, loading, error } = useQuery(FAMILY_VENDOR_OFFERINGS_QUERY, { variables: { category: 'catering' }, fetchPolicy: 'cache-and-network' });
  const { data: defsData } = useQuery(VENDOR_OFFERING_FIELD_DEFS_QUERY);
  const labels = packageLabelsFor(defsData?.vendorOfferingFields, 'catering');
  const n = Number(plates) > 0 ? Number(plates) : 0;

  // One row per menu
  const menus = useMemo(() => (data?.vendorOfferings || []).flatMap(o => (o.packages || []).map(p => {
    const d = o.details || {};
    const sections = [];
    (p.items || []).forEach(i => {
      const key = i.section || 'Other';
      let s = sections.find(x => x.name === key);
      if (!s) { s = { name: key, dishes: [] }; sections.push(s); }
      s.dishes.push(i.name);
    });
    return {
      key: `${o.id}|${p.id}`, offering: o, pkg: p, caterer: o.businessName, city: o.city,
      foodType: d.foodType || '', cuisines: d.cuisineTypes || [], styles: d.serviceStyles || [],
      minGuests: d.minGuests, maxGuests: d.maxGuests, tasting: d.tastingAvailable, staff: d.includesServingStaff,
      perUnit: PER_UNIT.test(p.priceUnit || ''), price: Number(p.price), dishes: (p.items || []).length, sections,
    };
  })), [data]);

  const totalFor = (m) => (m.perUnit ? m.price * n : m.price);
  const fits = (m) => !n || ((!m.minGuests || n >= m.minGuests) && (!m.maxGuests || n <= m.maxGuests));

  const foodTypes = [...new Set(menus.map(m => m.foodType).filter(Boolean))].sort();
  const cuisines = [...new Set(menus.flatMap(m => m.cuisines))].sort();
  const q = search.trim().toLowerCase();
  const rows = menus
    .filter(m => (!foodType || m.foodType === foodType) && (!cuisine || m.cuisines.includes(cuisine)) && (!q || [
      m.caterer, m.pkg.name, m.city, ...m.sections.flatMap(s => s.dishes),
    ].some(t => (t || '').toLowerCase().includes(q))))
    .sort((a, b) => ({
      total: totalFor(a) - totalFor(b),
      plate: a.price - b.price,
      dishes: b.dishes - a.dishes,
      caterer: a.caterer.localeCompare(b.caterer) || a.pkg.name.localeCompare(b.pkg.name),
    }[sortBy]));
  const lowest = rows.filter(fits).length ? Math.min(...rows.filter(fits).map(totalFor)) : null;
  const compare = selected.map(k => menus.find(m => m.key === k)).filter(Boolean);
  const toggle = (key) => setSelected(s => (s.includes(key) ? s.filter(k => k !== key) : s.length < MAX_COMPARE ? [...s, key] : s));

  const guestRange = (m) => (m.minGuests || m.maxGuests ? `${m.minGuests || '?'}–${m.maxGuests || '?'} guests` : '—');
  const yesNo = (v) => (v ? <CheckIcon fontSize="small" color="success" /> : <CloseIcon fontSize="small" color="disabled" />);

  return (
    <Box>
      {/* Controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField label="Number of plates" type="number" size="small" value={plates} onChange={e => setPlates(e.target.value)}
            inputProps={{ min: 1 }} sx={{ width: { md: 170 } }} />
          <TextField size="small" sx={{ flex: 1 }} value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search caterer, menu or dish (e.g. biryani)"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Food type" value={foodType} onChange={e => setFoodType(e.target.value)} sx={{ minWidth: 190 }}>
            <MenuItem value="">Any</MenuItem>
            {foodTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Cuisine" value={cuisine} onChange={e => setCuisine(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">Any</MenuItem>
            {cuisines.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Sort by" value={sortBy} onChange={e => setSortBy(e.target.value)} sx={{ minWidth: 220 }}>
            {SORTS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
        </Stack>
      </Paper>

      {loading && !data && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {booked && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBooked(null)}>
          {booked.count > 1 ? `${booked.count} booking requests` : 'Booking request'} sent to {booked.businessName}. They'll be notified and confirm it.
        </Alert>
      )}
      {data && !menus.length && (
        <Alert severity="info">
          {canBook
            ? 'No caterer menus to compare yet. Caterers appear once catering is on a signed contract.'
            : 'No caterer with a signed contract has published menus yet.'}
        </Alert>
      )}

      {/* Side-by-side */}
      {compare.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
          <Stack direction="row" alignItems="center" sx={{ p: 1.5 }}>
            <Typography fontWeight={700} sx={{ flex: 1 }}>Side by side{n ? ` for ${n.toLocaleString('en-IN')} plates` : ''}</Typography>
            <Button size="small" onClick={() => setSelected([])}>Clear</Button>
          </Stack>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100', verticalAlign: 'top' } }}>
                <TableCell sx={{ width: 170 }} />
                {compare.map(m => (
                  <TableCell key={m.key}>
                    {m.pkg.name}
                    <Typography variant="caption" display="block" color="text.secondary">{m.caterer} · {m.city}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <MuiTableBody>
              {[
                ['Total cost', m => <Typography fontWeight={700} color={totalFor(m) === Math.min(...compare.map(totalFor)) ? 'success.main' : 'inherit'}>{inr(totalFor(m))}</Typography>],
                ['Price', m => `${inr(m.price)} ${m.pkg.priceUnit}`],
                ['Guests catered', m => <Stack direction="row" spacing={0.5} alignItems="center">{yesNo(fits(m))}<span>{guestRange(m)}</span></Stack>],
                ['Food type', m => m.foodType || '—'],
                ['Cuisines', m => m.cuisines.join(', ') || '—'],
                ['Service', m => m.styles.join(', ') || '—'],
                ['Tasting session', m => yesNo(m.tasting)],
                ['Serving staff included', m => yesNo(m.staff)],
                ['Dishes', m => `${m.dishes}`],
              ].map(([label, cell]) => (
                <TableRow key={label}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{label}</TableCell>
                  {compare.map(m => <TableCell key={m.key}>{cell(m)}</TableCell>)}
                </TableRow>
              ))}
              {/* Dishes by course, across the compared menus */}
              {[...new Set(compare.flatMap(m => m.sections.map(s => s.name)))].map(section => (
                <TableRow key={section}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', verticalAlign: 'top' }}>{section}</TableCell>
                  {compare.map(m => (
                    <TableCell key={m.key} sx={{ verticalAlign: 'top' }}>
                      {(m.sections.find(s => s.name === section)?.dishes || []).join(', ') || <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {canBook && (
                <TableRow>
                  <TableCell />
                  {compare.map(m => (
                    <TableCell key={m.key}>
                      <Button size="small" variant="contained" onClick={() => setBooking({ offering: m.offering, packageId: m.pkg.id })}>Book this menu</Button>
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </MuiTableBody>
          </Table>
        </Paper>
      )}

      {/* All menus */}
      {rows.length > 0 && (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                <TableCell padding="checkbox">
                  <Tooltip title={`Tick up to ${MAX_COMPARE} to compare side by side`}><span>Compare</span></Tooltip>
                </TableCell>
                <TableCell>Menu</TableCell>
                <TableCell>Caterer</TableCell>
                <TableCell>Food type</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Total{n ? ` for ${n.toLocaleString('en-IN')}` : ''}</TableCell>
                <TableCell align="center">Dishes</TableCell>
                <TableCell>Guests</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(m => (
                <TableRow key={m.key} hover selected={selected.includes(m.key)}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.includes(m.key)} onChange={() => toggle(m.key)}
                      disabled={!selected.includes(m.key) && selected.length >= MAX_COMPARE} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{m.pkg.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.sections.map(s => s.name).join(' · ')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{m.caterer}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.city}</Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{m.foodType || '—'}</Typography></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{inr(m.price)} <Typography variant="caption" color="text.secondary">{m.pkg.priceUnit}</Typography></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" fontWeight={700}>{inr(totalFor(m))}</Typography>
                    {fits(m) && totalFor(m) === lowest && <Chip size="small" color="success" label="Lowest" sx={{ height: 18 }} />}
                  </TableCell>
                  <TableCell align="center">{m.dishes}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {n > 0 && (fits(m)
                        ? <CheckIcon fontSize="small" color="success" />
                        : <Tooltip title={`Caters ${guestRange(m)}`}><CloseIcon fontSize="small" color="error" /></Tooltip>)}
                      <Typography variant="caption">{guestRange(m)}</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      {data && menus.length > 0 && !rows.length && <Alert severity="info">No menus match these filters.</Alert>}

      <BookVendorDialog
        open={Boolean(booking)}
        offering={booking?.offering}
        initialPackageId={booking?.packageId}
        labels={labels}
        forFamily={isFamilyUser ? null : family?.displayName}
        onClose={() => setBooking(null)}
        onBooked={(b, all) => { setBooking(null); setBooked({ ...b, count: all?.length || 1 }); }}
      />
    </Box>
  );
}

export default function CatererComparisonPage() {
  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h4" fontWeight={700}>Caterer Cost Comparison</Typography>
        <Typography variant="body2" color="text.secondary">
          Every menu from the contracted caterers, costed for your number of plates. Tick up to {MAX_COMPARE} to compare their
          price, dishes and service side by side, then book the one you want.
        </Typography>
      </Box>
      <MenuComparison />
    </Box>
  );
}
